import React, { Fragment, useEffect, useState } from "react";

import { Col, Flex, Form, Row, Select } from "antd";

import Container from "@/components/container/Container";
import CardComponent from "@/components/card";
import ButtonComponent from "@/components/button/Button";
import IconCross from "@/components/icons/IconCross";

import { getAllCountries } from "@/services/common/getAllCountries";
import "@/styles/pages/profile/study-preferences.scss";
import { getDisciplines } from "@/services/common/getDisciplines";
import { getLevels } from "@/services/common/getLevels";
import { getRegionByCountry } from "@/services/common/getRegionByCountry";
import { getUniversityByCountryAndRegion } from "@/services/common/getUniversityByCountryAndRegion";
import BackWithLabel from "@/components/common/BackWithLabel";
import { getUserPreferences } from "@/services/users/getUserPreferences";
import { updatePreferences } from "@/services/users/updatePreferences";
import useTriggerNotification from "@/hooks/useTriggerNotification";
import { setPreferences } from "@/services/users/setPreferences";
import useUnsavedChanges from "@/hooks/useUnsavedChanges";

const tagRender = (props, isFormDisabled) => {
  const { label, onClose } = props;

  const onPreventMouseDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  return (
    <div
      className={isFormDisabled ? "disabled selected-chip" : "selected-chip"}
      onMouseDown={onPreventMouseDown}
    >
      <span>{label}</span>{" "}
      <IconCross
        style={{ cursor: `${isFormDisabled ? "" : "pointer"}` }}
        color={isFormDisabled ? "#6d6e75" : "#db5800"}
        onClick={!isFormDisabled ? onClose : null}
      />
    </div>
  );
};

const customizeRequiredMark = (label, { required }) => (
  <>
    {label}
    {required && <span style={{ color: "red" }}>*</span>}
  </>
);

const filterOption = (input, option) =>
  (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

import getCountryISO2 from "@/lib/data/isoThreeToTwo";
const { Option } = Select;

const StudyPreferences = () => {
  const [isFormDisabled, setIsFormDisabled] = useState(true);

  const [countryList, setCountryList] = useState([]);
  const [regionOptions, setRegionOption] = useState([]);
  const [levelOptions, setLevelOptions] = useState([]);
  const [disciplineOption, setDisciplineOptions] = useState([]);
  const [universityOptions, setUniverityOption] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState();
  const [selectedRegion, setSelectedRegion] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState();
  const [selectedDiscipline, setSelectedDiscipline] = useState();
  const [selectedUniversity, setSelectedUniversity] = useState();
  const [isNew, setIsNew] = useState(false);

  const { Option } = Select;
  const [form] = Form.useForm();
  const { trigger, element } = useTriggerNotification();

  const fetchCountryData = () => {
    getAllCountries()
      .then((response) => {
        setCountryList(
          response?.map((x) => ({
            ...x,
            flag:
              x.code !== "0"
                ? `https://cdn.jsdelivr.net/npm/country-flag-emoji-json@2.0.0/dist/images/${getCountryISO2(
                    x.code
                  )}.svg`
                : "/assets/images/otherFlag.png",
          }))
        );
      })
      .catch((error) => console.log(error));
  };

  const fetchLevels = () => {
    getLevels().then((response) => {
      setLevelOptions(response?.map((x) => ({ label: x.name, value: x.id })));
    });
  };

  const fetchDiscipline = () => {
    getDisciplines().then((response) => {
      setDisciplineOptions(response?.map((x) => ({ label: x.name, value: x.id })));
    });
  };

  useEffect(() => {
    fetchCountryData();
    fetchLevels();
    fetchDiscipline();
  }, []);

  const fetchRegionData = () => {
    getRegionByCountry(selectedCountry)
      .then((response) => {
        setRegionOption([]);
        setRegionOption(response?.map((x) => ({ label: x.name, value: x.id })));
        form.resetFields(["regionIds"]);
        form.resetFields(["universityIds"]);
        setSelectedRegion([]);
        setSelectedUniversity([]);
        setUniverityOption([]);
      })
      .catch((error) => console.log(error));
  };

  const fetchUniversity = () => {
    if (selectedCountry && selectedRegion) {
      getUniversityByCountryAndRegion(selectedCountry, selectedRegion)
        .then((response) => {
          setUniverityOption(response?.map((x) => ({ label: x.name, value: x.id })));
        })
        .catch((error) => console.log(error));
    }
  };

  useEffect(() => {
    if (selectedCountry?.length) {
      fetchRegionData();
    }
  }, [selectedCountry]);

  useEffect(() => {
    fetchUniversity();
  }, [selectedRegion]);

  const handleFinish = (value) => {
    if (isNew) {
      setPreferences({
        ...value,
        levelIds: value.levelIds ? [value.levelIds] : [],
        disciplineIds: value.disciplineIds ? [value.disciplineIds] : [],
      })
        .then((res) => {
          console.log(res);
          setIsNew(false);
          trigger({
            type: "success",
            message: "My Study Preferences updated successfully",
          });
          setIsFormDisabled(true);
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      console.log(value);
      updatePreferences({
        ...value,
        levelIds: value.levelIds
          ? Array.isArray(value.levelIds)
            ? value.levelIds
            : [value.levelIds]
          : [],
        disciplineIds: value.disciplineIds
          ? Array.isArray(value.disciplineIds)
            ? value.disciplineIds
            : [value.disciplineIds]
          : [],
      })
        .then((res) => {
          console.log(res);
          trigger({
            type: "success",
            message: "Study Preferences updated successfully",
          });
          setIsFormDisabled(true);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  const fetchData = () => {
    getUserPreferences()
      .then((res) => {
        setSelectedCountry(res.country.code);
        setSelectedRegion(res.regions?.map((x) => x.id));
        setSelectedLevel(res.levels?.map((x) => x.id));
        setSelectedDiscipline(res.disciplines?.map((x) => x.id));
        setSelectedUniversity(res.universities?.map((x) => x.id));
        form.setFieldsValue({
          countryCode: res.country.code,
          regionIds: res.regions?.map((x) => x.id),
          levelIds: res.levels?.map((x) => x.id),
          disciplineIds: res.disciplines?.map((x) => x.id),
          universityIds: res.universities?.map((x) => x.id),
        });
      })
      .catch((err) => {
        setIsNew(true);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  useUnsavedChanges(!isFormDisabled);

  return (
    <Fragment>
      {element}
      <div className="study-preferences-page">
        <Container>
          <CardComponent>
            <Row>
              <Col xxl={24}>
                <div className="heading">
                  <BackWithLabel
                    label={`${!isFormDisabled ? "Edit" : ""} My Study Preferences`}
                    path="/profile"
                  />
                </div>
              </Col>
            </Row>

            <Form
              name=""
              initialValues={{
                remember: true,
                countryCode: selectedCountry,
                regionIds: selectedRegion,
                levelIds: selectedLevel,
                disciplineIds: selectedDiscipline,
                universityIds: selectedUniversity,
              }}
              form={form}
              layout="vertical"
              onFinish={handleFinish}
              requiredMark={customizeRequiredMark}
            >
              <Row gutter={[16, 0]} className={isFormDisabled ? "" : "sideborder"}>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item
                    label="Preferred Country"
                    name="countryCode"
                    rules={[
                      {
                        required: true,
                        message: "Please select Preferred Country",
                      },
                    ]}
                  >
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a Preferred Country"
                      className="w-100"
                      onChange={(value) => {
                        setSelectedCountry(value);
                        setSelectedRegion([]);
                        setSelectedUniversity([]);
                      }}
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {countryList.map((country) => {
                        return (
                          <Option key={country.name} value={country.code} label={country.name}>
                            <Flex justify="start" align="center" gap={"5px"}>
                              <img
                                src={country.flag}
                                height={30}
                                width={30}
                                className={isFormDisabled && "disabled-image"}
                              />
                              <span>{country.name}</span>
                            </Flex>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Preferred Location" name="regionIds">
                    <Select
                      virtual={false}
                      mode="multiple"
                      maxTagCount={2}
                      maxCount={4}
                      placeholder="Select a Preferred Location"
                      className="w-100  multiSelect"
                      defaultBg={"#fff5f0"}
                      defaultColor={"#DB5800"}
                      tagRender={(props) => tagRender(props, isFormDisabled)}
                      options={regionOptions}
                      filterOption={filterOption}
                      onChange={(value) => setSelectedRegion(value)}
                      disabled={isFormDisabled}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Level" name="levelIds">
                    <Select
                      virtual={false}
                      showSearch
                      placeholder="Select a Level"
                      className="w-100"
                      filterOption={filterOption}
                      disabled={isFormDisabled}
                    >
                      {levelOptions.map((country) => {
                        return (
                          <Option key={country.value} value={country.value} label={country.label}>
                            <div>{country.label}</div>
                          </Option>
                        );
                      })}
                    </Select>
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Preferred Discipline" name="disciplineIds">
                    <Select
                      virtual={false}
                      maxTagCount={2}
                      maxCount={4}
                      placeholder="Select a Discipline"
                      className="w-100 multiSelect"
                      defaultBg={"#fff5f0"}
                      defaultColor={"#DB5800"}
                      tagRender={tagRender}
                      options={disciplineOption}
                      //   onChange={(value) => handleChange(value, "disciplines")}
                      disabled={isFormDisabled}
                    />
                  </Form.Item>
                </Col>
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <Form.Item label="Preferred University" name="universityIds">
                    <Select
                      virtual={false}
                      mode="multiple"
                      maxTagCount={2}
                      maxCount={4}
                      placeholder="Select a university"
                      className="w-100 multiSelect"
                      defaultBg={"#fff5f0"}
                      defaultColor={"#DB5800"}
                      tagRender={(props) => tagRender(props, isFormDisabled)}
                      options={universityOptions}
                      filterOption={filterOption}
                      //   onChange={(value) => handleChange(value, "university")}
                      disabled={isFormDisabled}
                    />
                  </Form.Item>
                </Col>
              </Row>
              {!isFormDisabled && (
                <Row justify="center">
                  <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                    <ButtonComponent
                      label="Save Preferences"
                      varient="primary"
                      htmlType="submit"
                      className="w-100"
                    />
                  </Col>
                </Row>
              )}
            </Form>
            {isFormDisabled && (
              <Row justify="center">
                <Col xxl={8} xl={8} lg={12} md={12} sm={24} xs={24}>
                  <ButtonComponent
                    label="Edit Preferences"
                    varient="primary"
                    htmlType="button"
                    onClick={() => setIsFormDisabled(false)}
                    className="w-100"
                  />
                </Col>
              </Row>
            )}
          </CardComponent>
        </Container>
      </div>
    </Fragment>
  );
};

export default StudyPreferences;
